<script setup>
import { computed, ref, watch } from 'vue';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import draggable from 'vuedraggable';

const { question, disabled } = defineProps({ question: Object, disabled: Boolean });
const modelValue = defineModel();

const contents = ref(
    question.answers.map((a) => ({
        content_id: a.content_id,
        content: a.content,
        isCorrect: a.isCorrect,
    }))
);

const dragOptions = computed(() => ({
    animation: 200,
    group: 'contents',
    disabled: false,
    ghostClass: 'ghost',
}));

watch(
    contents,
    (newContents) => {
        modelValue.value = newContents.map((c, index) => ({
            key_id: question.answers[index].id,
            content_id: c.content_id,
        }));
    },
    { deep: true, immediate: true }
);

watch(
    () => question.answers,
    (newAnswers) => {
        contents.value = newAnswers.map((a) => ({
            content_id: a.content_id,
            content: a.content,
            isCorrect: a.isCorrect,
        }));
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <div class="matching-content">
        <div class="left-side">
            <div v-for="item in question.answers" :key="item.id" class="answer-variant">
                <p v-html="generateHTML(item.key, [StarterKit, Image])"></p>
            </div>
        </div>

        <div class="right-side">
            <draggable v-model="contents" item-key="content_id" v-bind="dragOptions" class="answer-variants" :disabled="disabled">
                <template #item="{ element }">
                    <div
                        class="answer-variant"
                        :class="{
                            disabled: disabled,
                            correct: element.isCorrect,
                            incorrect: element.isCorrect == false,
                        }"
                    >
                        <p v-html="generateHTML(element.content, [StarterKit])"></p>
                        <Button icon="pi pi-arrows-alt" variant="text" class="drag-handler" />
                    </div>
                </template>
            </draggable>
        </div>
    </div>
</template>

<style scoped>
.matching-content {
    @apply flex items-center gap-6 w-full;
}
.left-side,
.right-side {
    @apply w-1/2 flex flex-col gap-6;
}
.answer-variants {
    @apply flex flex-col gap-6;
}
.answer-variant {
    @apply w-full p-4 border border-gray-200 bg-white rounded-xl flex items-center gap-4 justify-between min-h-[68px];
}
.answer-variant.disabled {
    @apply bg-gray-100 pointer-events-none;
}
.answer-variant.correct {
    @apply bg-green-100;
}
.answer-variant.incorrect {
    @apply bg-red-100;
}
</style>
